import type { Color, Marker, SgfData } from './sgf.js';

export type { Color, Marker, MarkerType } from './sgf.js';

/** 0 = empty, 1 = black, 2 = white */
export type Point = 0 | Color;
export type BoardState = Point[][];

export interface LastMove {
  x: number;
  y: number;
}

export interface NodeMove {
  x: number;
  y: number;
  color: Color;
  pass: boolean;
}

export interface GameNode {
  id: string;
  state: BoardState;
  turn: Color;
  lastMove: LastMove | null;
  move: NodeMove | null;
  /** Markup (circles, squares, triangles, marks, labels) shown at this position. */
  marks: Marker[];
  parent: GameNode | null;
  children: GameNode[];
}

/** A plain-object mirror of GameNode without the `parent` back-reference, safe to serialize. */
export interface SerializedNode {
  id: string;
  state: BoardState;
  turn: Color;
  lastMove: LastMove | null;
  move: NodeMove | null;
  marks: Marker[];
  children: SerializedNode[];
}

function cloneState(state: BoardState): BoardState {
  return state.map((row) => row.slice()) as BoardState;
}

export class NodeIdGenerator {
  private counter = 0;
  next(): string {
    return `node-${++this.counter}`;
  }
}

export function createNode(
  state: BoardState,
  turn: Color,
  lastMove: LastMove | null,
  move: NodeMove | null,
  parent: GameNode | null,
  id: string,
  marks: Marker[] = []
): GameNode {
  return {
    id,
    state: cloneState(state),
    turn,
    lastMove: lastMove ? { x: lastMove.x, y: lastMove.y } : null,
    move: move ? { x: move.x, y: move.y, color: move.color, pass: move.pass } : null,
    marks: marks.map((m) => ({ ...m })),
    parent,
    children: []
  };
}

export function serializeTree(node: GameNode | null): SerializedNode | null {
  if (!node) return null;
  return {
    id: node.id,
    state: node.state,
    turn: node.turn,
    lastMove: node.lastMove,
    move: node.move,
    marks: node.marks,
    children: node.children.map((c) => serializeTree(c) as SerializedNode)
  };
}

export function deserializeTree(raw: SerializedNode | null, parent: GameNode | null = null): GameNode | null {
  if (!raw) return null;
  const node: GameNode = {
    id: raw.id,
    state: cloneState(raw.state),
    turn: raw.turn,
    lastMove: raw.lastMove ? { x: raw.lastMove.x, y: raw.lastMove.y } : null,
    move: raw.move ? { x: raw.move.x, y: raw.move.y, color: raw.move.color, pass: raw.move.pass } : null,
    marks: (raw.marks ?? []).map((m) => ({ ...m })),
    parent,
    children: []
  };
  if (Array.isArray(raw.children)) {
    for (const child of raw.children) {
      const childNode = deserializeTree(child, node);
      if (childNode) node.children.push(childNode);
    }
  }
  return node;
}

export function findNodeById(root: GameNode | null, id: string | null | undefined): GameNode | null {
  if (!root || !id) return null;
  if (root.id === id) return root;
  for (const child of root.children) {
    const found = findNodeById(child, id);
    if (found) return found;
  }
  return null;
}

interface Group {
  color: Color;
  stones: { x: number; y: number }[];
  liberties: number;
}

export function getGroup(board: BoardState, startX: number, startY: number, size: number): Group | null {
  const color = board[startY]?.[startX];
  if (!color) return null;

  const visited: boolean[][] = [];
  for (let i = 0; i < size; i++) visited.push(new Array(size).fill(false) as boolean[]);

  const stones: { x: number; y: number }[] = [];
  const liberties = new Set<string>();
  const queue: { x: number; y: number }[] = [{ x: startX, y: startY }];
  (visited[startY] as boolean[])[startX] = true;

  let head = 0;
  while (head < queue.length) {
    const pt = queue[head++] as { x: number; y: number };
    stones.push(pt);

    const neighbors = [
      { x: pt.x + 1, y: pt.y },
      { x: pt.x - 1, y: pt.y },
      { x: pt.x, y: pt.y + 1 },
      { x: pt.x, y: pt.y - 1 }
    ];

    for (const { x: nx, y: ny } of neighbors) {
      if (nx >= 0 && nx < size && ny >= 0 && ny < size) {
        const cell = (board[ny] as Point[])[nx];
        if (cell === 0) {
          liberties.add(`${nx},${ny}`);
        } else if (cell === color && !(visited[ny] as boolean[])[nx]) {
          (visited[ny] as boolean[])[nx] = true;
          queue.push({ x: nx, y: ny });
        }
      }
    }
  }

  return { color, stones, liberties: liberties.size };
}

export interface NextStateResult {
  state: BoardState;
  turn: Color;
  lastMove: LastMove;
}

/**
 * Plays a stone of `color` at (x, y) on top of `baseState`, resolves
 * captures, and returns the resulting state — or `null` if the move is
 * suicide (no captures happen and the played stone's own group would have
 * zero liberties). Does not enforce the ko rule.
 */
export function computeNextState(
  baseState: BoardState,
  size: number,
  mvX: number,
  mvY: number,
  color: Color
): NextStateResult | null {
  const st = cloneState(baseState);
  const opponent: Color = color === 1 ? 2 : 1;
  (st[mvY] as Point[])[mvX] = color;

  const neighbors = [
    { x: mvX + 1, y: mvY },
    { x: mvX - 1, y: mvY },
    { x: mvX, y: mvY + 1 },
    { x: mvX, y: mvY - 1 }
  ];

  let capturedStones: { x: number; y: number }[] = [];
  const checkedGroups = new Set<string>();

  for (const { x: nx, y: ny } of neighbors) {
    if (nx >= 0 && nx < size && ny >= 0 && ny < size && (st[ny] as Point[])[nx] === opponent) {
      const key = `${nx},${ny}`;
      if (!checkedGroups.has(key)) {
        const oppGroup = getGroup(st, nx, ny, size);
        if (oppGroup) {
          for (const stone of oppGroup.stones) {
            checkedGroups.add(`${stone.x},${stone.y}`);
          }
          if (oppGroup.liberties === 0) {
            capturedStones = capturedStones.concat(oppGroup.stones);
          }
        }
      }
    }
  }

  for (const cs of capturedStones) {
    (st[cs.y] as Point[])[cs.x] = 0;
  }

  const ownGroup = getGroup(st, mvX, mvY, size);
  if (ownGroup && ownGroup.liberties === 0) {
    return null;
  }

  return { state: st, turn: opponent, lastMove: { x: mvX, y: mvY } };
}

/**
 * Plays a stone of `color` at (x, y) from `node`, returning the resulting
 * child node — or `null` if the move is illegal (occupied point, or
 * suicide). If `node` already has this exact move as a child (the user
 * replaying a previously-explored branch), that child is reused and moved
 * to the end of the sibling list rather than duplicated.
 */
export function playMove(
  node: GameNode,
  size: number,
  x: number,
  y: number,
  color: Color,
  idGenerator: NodeIdGenerator
): GameNode | null {
  if (x < 0 || x >= size || y < 0 || y >= size) return null;
  if (node.state[y]?.[x] !== 0) return null;

  const existingChildIndex = node.children.findIndex(
    (child) => child.move && !child.move.pass && child.move.x === x && child.move.y === y && child.move.color === color
  );

  if (existingChildIndex !== -1) {
    const existingChild = node.children.splice(existingChildIndex, 1)[0] as GameNode;
    node.children.push(existingChild);
    return existingChild;
  }

  const result = computeNextState(node.state, size, x, y, color);
  if (!result) return null; // suicide move

  const newNode = createNode(result.state, result.turn, result.lastMove, { x, y, color, pass: false }, node, idGenerator.next());
  node.children.push(newNode);
  return newNode;
}

export interface InitialTree {
  rootNode: GameNode;
  currentNode: GameNode;
}

/**
 * Builds the move tree for a fresh game: an empty (or SGF-setup) board as
 * the root, followed by one child per SGF move. `targetMove` (1-indexed, 0
 * meaning the root/empty board) selects which node becomes `currentNode`;
 * omitted/out-of-range targets fall back to the last move played.
 */
export function buildInitialTree(
  size: number,
  sgfData: SgfData | null,
  targetMove: number | null,
  idGenerator: NodeIdGenerator = new NodeIdGenerator()
): InitialTree {
  const rootState: BoardState = [];
  for (let i = 0; i < size; i++) rootState.push(new Array(size).fill(0) as Point[]);
  let initTurn: Color = 1;

  if (sgfData) {
    for (const stone of sgfData.setup) {
      if (stone.x >= 0 && stone.x < size && stone.y >= 0 && stone.y < size) {
        (rootState[stone.y] as Point[])[stone.x] = stone.color;
      }
    }
    if (sgfData.initialTurn) {
      initTurn = sgfData.initialTurn;
    }
  }

  const rootNode = createNode(rootState, initTurn, null, null, null, idGenerator.next(), sgfData?.rootMarks ?? []);
  let currentNode = rootNode;

  if (sgfData && sgfData.moves.length > 0) {
    let parent = rootNode;
    let targetNode: GameNode | null = null;

    for (let m = 0; m < sgfData.moves.length; m++) {
      const mv = sgfData.moves[m] as SgfData['moves'][number];
      let node: GameNode | null;
      if (mv.pass) {
        const nextTurn: Color = mv.color === 1 ? 2 : 1;
        node = createNode(parent.state, nextTurn, parent.lastMove, { pass: true, color: mv.color, x: -1, y: -1 }, parent, idGenerator.next(), mv.marks);
      } else {
        const res = computeNextState(parent.state, size, mv.x, mv.y, mv.color);
        if (!res) continue;
        node = createNode(res.state, res.turn, res.lastMove, { x: mv.x, y: mv.y, color: mv.color, pass: false }, parent, idGenerator.next(), mv.marks);
      }
      parent.children.push(node);
      parent = node;
      if (targetMove !== null && m + 1 === targetMove) {
        targetNode = node;
      }
    }

    if (targetMove !== null) {
      if (targetMove === 0) {
        currentNode = rootNode;
      } else if (targetNode) {
        currentNode = targetNode;
      } else if (targetMove >= sgfData.moves.length) {
        currentNode = parent;
      } else {
        currentNode = rootNode;
      }
    } else {
      currentNode = parent;
    }
  }

  return { rootNode, currentNode };
}
